import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import { useSelector } from "react-redux";
import { Reservation } from "../../../../types/bookingCreation";
import moment from "moment-timezone";
import { couponRedemptionLoading } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { memo, useCallback, useMemo, useState, useEffect, Fragment, useId } from "react";
import { useApplyCoupon } from "../../../../redux/flows/applyCouponFlow";
import {
	calculateDisplayPrice,
	consolidateCoupons,
	findCoupons,
	isFreeOfCharge,
} from "../../../../util/payments";
import { formatCurrency } from "../../../../util";
import { cn } from "lib/utils";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { SpinnerOverlay } from "components/ui/spinner";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { useTaxStatement } from "../../../../util/taxStatement";
import { atStation } from "../../../../util/stationTime";
import { useSelectedLocationTimeZone } from "../../../../util/locationTimeZone";

interface KeyHandoverTicketProps {
	location?: string;
	reservations: Reservation[];
}

/**
 * Renders decorative ticket hole punch effect on the side of the ticket
 */
const TicketHole = ({ side, uniqueId }: { side: "left" | "right"; uniqueId: string }) => {
	const clipPathId = `${side}Hole-${uniqueId}`;
	const isRight = side === "right";
	
	return (
		<svg 
			className={cn(
				"absolute top-[29%] w-4 h-4 z-10 pointer-events-none",
				isRight ? "right-[-8px]" : "left-[-8px]"
			)} 
			viewBox="0 0 16 16"
		>
			<defs>
				<clipPath id={clipPathId}>
					<rect x={isRight ? "0" : "8"} y="0" width="8" height="16" />
				</clipPath>
			</defs>
			{/* White rectangle to cover the border at the hole */}
			<rect x={isRight ? "5" : "7"} y="0" width="4" height="16" fill="white" />
			{/* Background fill without stroke to avoid double border */}
			<circle cx="8" cy="8" r="8" fill="rgb(249, 250, 251)" clipPath={`url(#${clipPathId})`} />
			{/* Only draw the outer arc border */}
			<path 
				d={isRight ? "M 8 0.5 A 7.5 7.5 0 0 0 8 15.5" : "M 8 0.5 A 7.5 7.5 0 0 1 8 15.5"} 
				stroke="rgba(0, 0, 0, 0.1)" 
				strokeWidth="1" 
				fill="none" 
				strokeLinecap="round" 
			/>
		</svg>
	);
};

interface ReservationItemProps {
	reservation: Reservation;
	selectedService: string | null;
	dateTimeFormat: string;
	normalizeEndForDisplay: (end: moment.Moment) => moment.Moment;
	language: string;
	/** The booked location's zone (#3219). */
	timeZone: string;
}

/**
 * Renders a single reservation item with pickup/return details
 */
const ReservationItem = ({ 
	reservation, 
	selectedService, 
	dateTimeFormat, 
	normalizeEndForDisplay,
	language,
	timeZone
}: ReservationItemProps) => {
	const { t } = useTranslation();
	const textBoldClass = "text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words";
	
	// Parse ISO date strings and read them on the station's clock
	const reservationStartDate = atStation(reservation.reservationItems[0].fromDate, timeZone);
	const reservationEndDate = atStation(reservation.reservationItems[0].toDate, timeZone);

	const returnReservationStartDate = reservation.reservationItems[1]?.fromDate 
		? atStation(reservation.reservationItems[1].fromDate, timeZone)
		: moment();
	const returnReservationEndDate = reservation.reservationItems[1]?.toDate
		? atStation(reservation.reservationItems[1].toDate, timeZone)
		: moment();

	const price = reservation.payment.totalPrice;
	
	const formatDateRange = (start: moment.Moment, end: moment.Moment) => 
		`${start.format(dateTimeFormat)} – ${normalizeEndForDisplay(end).format(dateTimeFormat)}`;

	if (selectedService === "Key-Pickup-And-Return") {
		return (
			<Fragment>
				<div className="w-full flex flex-col mb-1.5 border-foreground/45">
					<div className={cn(textBoldClass, "flex w-full flex-row justify-between")}>
						<span>{t("book_location.keys.summary.key_pickup")}</span>
						<span>{formatCurrency(price, language)}</span>
					</div>
					<div>
						<p className="text-sm">
							{formatDateRange(reservationStartDate, reservationEndDate)}
						</p>
					</div>
				</div>
				<div className="w-full flex flex-col mb-1.5 pb-3 border-b border-foreground/45">
					<div className={cn(textBoldClass, "flex w-full flex-row justify-between")}>
						<span>{t("book_location.keys.summary.key_return")}</span>
					</div>
					<div>
						<p className="text-sm">
							{formatDateRange(returnReservationStartDate, returnReservationEndDate)}
						</p>
					</div>
				</div>
			</Fragment>
		);
	}

	return (
		<div className="w-full flex flex-col mb-1.5 border-foreground/45">
			<div className={cn(textBoldClass, "flex w-full flex-row justify-between")}>
				<span>{t("book_location.keys.summary.key_pickup")}</span>
				<span>{formatCurrency(price, language)}</span>
			</div>
			<div>
				<p className="text-sm">
					{formatDateRange(reservationStartDate, reservationEndDate)}
				</p>
			</div>
		</div>
	);
};

// Ticket-style summary for key handover
const KeyHandoverTicket = (props: KeyHandoverTicketProps) => {
	const { location, reservations } = props;
	const routerLocation = useLocation();
	const searchParams = new URLSearchParams(routerLocation.search);
	const hasCustomerAgreementId = !!searchParams.get("customerAgreementId");
	const [couponCode, setCouponCode] = useState<string>("");
	const landlordData = useSelector((state: RootState) => state.bookingCreation.landlordData);
	// The booked location's clock (#3219).
	const timeZone = useSelectedLocationTimeZone();
	const { t, i18n } = useTranslation();
	const taxStatement = useTaxStatement();
	const dispatch = useAppDispatch();
	const uniqueId = useId();
	
	// Keep moment locale in sync with i18n and derive a locale-aware format string
	useEffect(() => {
		moment.locale(i18n.language);
	}, [i18n.language]);

	const dateTimeWithWeekday = useMemo(() => {
		const localeData = moment.localeData(i18n.language);
		const dateL = localeData.longDateFormat("L");
		const timeLT = localeData.longDateFormat("LT");
		// Remove year tokens (YY or YYYY) and adjacent separators
		const dateNoYear = dateL
			.replace(/[\s./-]*Y{2,4}/gi, "")
			.replace(/\s{2,}/g, " ")
			.trim();
		// Example (de): "dd DD.MM. LT"  ->  "Mo 04.11. 14:00"
		// Example (en): "dd MM/DD LT"   ->  "Mo 11/04 2:00 PM"
		return `dd ${dateNoYear} ${timeLT}`;
	}, [i18n.language]);

	// Normalize end display times that use ':59' as an inclusive boundary (UI should show the next full minute/hour)
	const normalizeEndForDisplay = useCallback((end: moment.Moment) => {
		return end.minutes() === 59
			? end.clone().add(1, "minute").startOf("minute")
			: end;
	}, []);
	const tenantConfig = useSelector(
		(state: RootState) => state.tenantConfig.config
	);
	const selectedService = useSelector(
		(state: RootState) => state.bookingCreation.keyHandoverSelectedService
	);

	const isCouponRedemptionLoading = useSelector(
		(state: RootState) => state.bookingCreation.loading.couponRedemption
	);

	const applyCoupon = useApplyCoupon();

	// Basic coupon validation - alphanumeric with optional dashes/underscores
	const isValidCoupon = useCallback((code: string) => {
		return /^[a-zA-Z0-9-_ ]+$/.test(code) && code.trim().length > 0;
	}, []);

	const isApplyButtonDisabled = useMemo(
		() => !isValidCoupon(couponCode),
		[couponCode, isValidCoupon]
	);

	const onApplyCoupon = useCallback(async () => {
		if (isApplyButtonDisabled) return;

		dispatch(couponRedemptionLoading({ loading: true }));

		const reservationIds = reservations.map((reservation) => reservation.reservationId);

		await applyCoupon(reservationIds, couponCode.trim(), landlordData.email);

		setCouponCode("");
	}, [applyCoupon, couponCode, dispatch, isApplyButtonDisabled, reservations, landlordData.email]);

	const coupons = useMemo(() => findCoupons(reservations), [reservations]);
	const totalPrice = reservations[0]?.payment.totalPrice ?? 0;

	const displayPrice = useMemo(
		() => calculateDisplayPrice(totalPrice, coupons),
		[totalPrice, coupons]
	);

	// A promo code cannot reduce a free booking. ADR-0008 amendment 2026-09-16.
	// Reads the RAW amount, not `totalPrice` above: that one is `?? 0`, which
	// turns "no reservation yet" into a zero this rule would call free.
	const free = isFreeOfCharge(
		reservations[0]?.payment.totalPrice,
		reservations.length
	);
	const showCouponButton =
		tenantConfig?.allowCoupons && coupons.length === 0 && !hasCustomerAgreementId && !free;

	const consolidatedCoupons = useMemo(
		() => consolidateCoupons(coupons),
		[coupons]
	);
	const textBoldClass =
		"text-foreground/80 font-bold leading-5 tracking-[0.02em] break-words";

	return (
		<div className="relative overflow-clip">
			{/* Ticket holes as decorative SVG half-circles */}
			<TicketHole side="right" uniqueId={uniqueId} />
			<TicketHole side="left" uniqueId={uniqueId} />
			
			<div className="bg-white flex flex-col py-8 px-5 border border-foreground/10 relative rounded-md text-foreground">
				<div>
					<p>{t("orders.ticket_overview.location")}</p>
					<p className={textBoldClass}>{location}</p>
				</div>
			<div className="mt-2">
				<p>{t("orders.ticket_overview.booked_items")}</p>
				{reservations.map((reservation, idx) => {
					const itemKey = reservation.reservationId || reservation.id || idx;
					return (
						<ReservationItem 
							key={itemKey}
							reservation={reservation}
							selectedService={selectedService}
							dateTimeFormat={dateTimeWithWeekday}
							normalizeEndForDisplay={normalizeEndForDisplay}
							language={i18n.language}
							timeZone={timeZone}
						/>
					);
				})}
			</div>

			<div>
				{!hasCustomerAgreementId && consolidatedCoupons.map((coupon) => (
					<div className="flex flex-row justify-between" key={coupon.code}>
						<p>
							{t("book_location.confirmation.coupon_item")} ({coupon.code})
						</p>
						<p className={textBoldClass}>
							{!isCouponRedemptionLoading
								? formatCurrency(
										(coupon.amount > totalPrice ? totalPrice : coupon.amount) *
											-1,
										i18n.language
									)
								: "..."}
						</p>
					</div>
				))}

				<div className="flex flex-row justify-between">
					<p>{t("book_location.confirmation.total_items")}</p>
					<p className={textBoldClass}>
						{!isCouponRedemptionLoading
							? formatCurrency(displayPrice, i18n.language)
							: "..."}
					</p>
				</div>
				{showCouponButton && (
					<div className="flex flex-row justify-between items-center gap-4 my-2">
						<Input
							type="text"
							placeholder={t("general.enter_coupon_code")}
							value={couponCode}
							onChange={(e) => setCouponCode(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									void onApplyCoupon();
								}
							}}
							inputSize="sm"
							className="w-full"
							aria-label={t("general.enter_coupon_code")}
						/>
						<Button
							onClick={onApplyCoupon}
							disabled={isApplyButtonDisabled || isCouponRedemptionLoading}
							variant="primary"
							size="sm"
							aria-busy={isCouponRedemptionLoading}
						>
							{t("orders.ticket_overview.button")}
						</Button>
					</div>
				)}
				{/* No price, nothing for a tax statement to be about. */}
				{!free && (
					<p className="text-sm">
						{taxStatement.sentence}
					</p>
				)}
			</div>

			{isCouponRedemptionLoading && <SpinnerOverlay />}
			</div>
		</div>
	);
};
export default memo(KeyHandoverTicket);
